import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import CaseSharingBadge from "../components/CaseSharingBadge";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import IntakeTab from "../components/case-tabs/IntakeTab";
import QuestionnaireTab from "../components/case-tabs/QuestionnaireTab";
import TimelineTab from "../components/case-tabs/TimelineTab";
import EvidenceTab from "../components/case-tabs/EvidenceTab";
import DraftingTab from "../components/case-tabs/DraftingTab";
import ReviewTab from "../components/case-tabs/ReviewTab";
import ShareTab from "../components/case-tabs/ShareTab";
import PreviewTab from "../components/case-tabs/PreviewTab";
import { useMarketplaceEnabled } from "../context/ConfigContext";

const ALL_TABS = [
  { id: "intake", label: "1. Intake" },
  { id: "questionnaire", label: "2. Questionnaire" },
  { id: "timeline", label: "3. Timeline" },
  { id: "evidence", label: "4. Evidence" },
  { id: "drafting", label: "5. Drafting" },
  { id: "review", label: "6. Review" },
  { id: "share", label: "7. Share", marketplaceOnly: true },
  { id: "preview", label: "8. Preview & Export" },
];

export default function CaseEditor() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [tab, setTab] = useState("intake");
  const [saving, setSaving] = useState(false);
  const marketplaceEnabled = useMarketplaceEnabled();

  const TAB_DEFS = ALL_TABS
    .filter((t) => !t.marketplaceOnly || marketplaceEnabled)
    .map((t, i) => ({ ...t, label: t.label.replace(/^\d+\. /, `${i + 1}. `) }));

  const load = async () => {
    try {
      const r = await api.get(`/cases/${id}`);
      setCaseData(r.data);
    } catch (e) {
      toast.error("Could not load case");
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveCase = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put(`/cases/${id}`, patch);
      setCaseData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!caseData) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="max-w-[1400px] mx-auto px-6 py-16 label-tiny" data-testid="case-loading">Loading case…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-2" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> All cases
            </Link>
            <h1 className="font-serif-display text-3xl md:text-4xl text-slate-900 leading-tight" data-testid="case-name-heading">{caseData.name}</h1>
            <div className="label-tiny mt-1 tabular-nums">
              Status: {caseData.status} · Updated {new Date(caseData.updated_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {marketplaceEnabled && <CaseSharingBadge caseData={caseData} onChange={(c) => setCaseData(c)} />}
            {saving && <span className="label-tiny" data-testid="saving-indicator">Saving…</span>}
            <FloppyDisk className="w-4 h-4 text-slate-400" />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="case-tabs">
          <TabsList className="bg-transparent p-0 h-auto rounded-none border border-slate-200 w-full overflow-x-auto flex justify-start">
            {TAB_DEFS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`tab-${t.id}`}
                className="rounded-none border-r border-slate-200 last:border-r-0 px-5 py-3 text-xs font-mono-tech uppercase tracking-wider data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-none"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="border border-t-0 border-slate-200 p-6 md:p-10 bg-white">
            <TabsContent value="intake" className="mt-0">
              <IntakeTab caseData={caseData} save={saveCase} />
            </TabsContent>
            <TabsContent value="questionnaire" className="mt-0">
              <QuestionnaireTab caseData={caseData} save={saveCase} />
            </TabsContent>
            <TabsContent value="timeline" className="mt-0">
              <TimelineTab caseData={caseData} save={saveCase} />
            </TabsContent>
            <TabsContent value="evidence" className="mt-0">
              <EvidenceTab caseData={caseData} reload={load} />
            </TabsContent>
            <TabsContent value="drafting" className="mt-0">
              <DraftingTab caseData={caseData} reload={load} save={saveCase} />
            </TabsContent>
            <TabsContent value="review" className="mt-0">
              <ReviewTab caseData={caseData} />
            </TabsContent>
            <TabsContent value="share" className="mt-0">
              {marketplaceEnabled && <ShareTab caseData={caseData} save={saveCase} reload={load} />}
            </TabsContent>
            <TabsContent value="preview" className="mt-0">
              <PreviewTab caseData={caseData} />
            </TabsContent>
          </div>
        </Tabs>
      </main>
      <SiteFooter />
    </div>
  );
}
